import { enterMaintenance, exitMaintenance } from "../maintenance/state";

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Выйти из impersonation (django-hijack). Эндпоинт `/hijack/release/` принимает
 * ТОЛЬКО POST + CSRF — GET-переход давал HTTP 405. Сабмитим скрытую POST-форму
 * (как родной hijack-toolbar); после релиза hijack делает 302 на `next` и
 * браузер возвращается в исходную учётку.
 *
 * `next = /admin/core/user/` — список пользователей, а не главная админки:
 * из замещения выходят, чтобы тут же зайти под следующим сотрудником, и
 * главная заставляла лишний раз кликать. Это основной путь выхода: плашку
 * django-hijack вставляет только в свои страницы, на SPA её нет, поэтому
 * кнопка в баннере приложения — то, чем пользуются в реальности.
 */
export function releaseHijack(): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = "/hijack/release/";
  form.style.display = "none";
  const addField = (name: string, value: string) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };
  addField("csrfmiddlewaretoken", getCookie("skedd_csrftoken") ?? "");
  addField("next", "/admin/core/user/");
  document.body.appendChild(form);
  form.submit();
}

/**
 * US-56: 5xx-ответы и network-errors переводят приложение в maintenance-mode.
 * 4xx — обычные бизнес-ошибки, maintenance не триггерят. /api/health/ —
 * пробник самого maintenance-overlay, отдельно обрабатывается там же
 * (вызывает exitMaintenance() напрямую).
 */
const MAINTENANCE_STATUS_CODES = new Set([502, 503, 504]);

/**
 * US-69: вытащить человекочитаемые сообщения из DRF-ответа validation-error.
 *
 * DRF возвращает 400 в одном из форматов:
 *  - `{"detail": "..."}` — общая ошибка (мы это уже обрабатываем выше);
 *  - `{"start_date": ["Дата начала не может быть в прошлом."], ...}` —
 *    serializer field validation;
 *  - `{"non_field_errors": ["..."]}` — serializer-level validate().
 *
 * Эта функция собирает первое сообщение каждого поля и объединяет их через
 * « · », чтобы получился короткий человеческий текст для Alert/плашки.
 * Если ничего полезного не нашлось — возвращает пустую строку, и вызывающий
 * код fall back-нет на «API ... failed: <status>».
 *
 * Имена полей мы НЕ префиксуем («start_date: ...») — Django-сообщения уже
 * пишутся с учётом контекста («Дата начала не может быть в прошлом»),
 * пользователю достаточно текста ошибки.
 */
export function extractFieldErrors(body: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [, val] of Object.entries(body)) {
    if (Array.isArray(val)) {
      for (const item of val) {
        if (typeof item === "string" && item.trim()) {
          parts.push(item.trim());
          break; // одно сообщение на поле — этого хватает
        }
      }
    } else if (typeof val === "string" && val.trim()) {
      parts.push(val.trim());
    }
  }
  return parts.join(" · ");
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.method && init.method.toUpperCase() !== "GET") {
    const csrf = getCookie("skedd_csrftoken");
    if (csrf) headers.set("X-CSRFToken", csrf);
  }
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  let response: Response;
  try {
    response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  } catch (networkError) {
    // TypeError: Failed to fetch — backend / nginx / весь интернет недоступны.
    // Шлём maintenance-сигнал и пробрасываем дальше, чтобы вызывающий код
    // мог показать локальный Alert (overlay покажется поверх).
    enterMaintenance("network");
    throw networkError;
  }
  if (MAINTENANCE_STATUS_CODES.has(response.status)) {
    enterMaintenance("server");
    // Текст видит пользователь (напр. на таймшите при сбое загрузки), поэтому
    // человеческий, а не «API /api/projects/ unavailable: 503». Слово
    // unavailable оставлено для тестов и логов — в скобках, в конце.
    throw new Error(
      `Сервис временно недоступен. Попробуйте через минуту. (unavailable: ${response.status})`,
    );
  }
  // Любой 2xx — повод снять maintenance, если оно было.
  if (response.ok) {
    exitMaintenance();
  }
  if (!response.ok) {
    let detail = "";
    let body: Record<string, unknown> | null = null;
    try {
      body = (await response.clone().json()) as Record<string, unknown>;
      if (body && typeof body["detail"] === "string") detail = body["detail"] as string;
      // US-69: DRF для field-validation возвращает `{"start_date": ["…"], "end_date": ["…"]}`
      // без `detail` — мы раньше показывали неинформативное «API ... failed: 400».
      // Собираем field-errors в одно человекочитаемое сообщение.
      if (!detail && body) {
        detail = extractFieldErrors(body);
      }
    } catch {
      // не JSON — используем статус
    }
    // 409 Conflict без detail: действие уже выполнено / статус успел измениться
    // (напр. коллега согласовал параллельно). Показываем понятную подсказку
    // вместо сырого «API … failed: 409».
    if (!detail && response.status === 409) {
      detail =
        "Действие уже выполнено или данные успели измениться. Обновите страницу и попробуйте снова.";
    }
    // 429 отдаёт nginx (limit_req) — тело HTML-ное, `detail` из него не достать.
    // Это не поломка и не простой: слишком частые запросы с одного адреса.
    // Отдельно от 502/503/504 (maintenance) — тут просто надо подождать.
    if (!detail && response.status === 429) {
      detail = "Слишком много запросов. Подождите минуту и попробуйте снова.";
    }
    const err = new Error(detail || `API ${path} failed: ${response.status}`) as Error & {
      status?: number;
      body?: Record<string, unknown> | null;
    };
    err.status = response.status;
    err.body = body;
    throw err;
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

export interface Me {
  id: number;
  username: string;
  email: string;
  /** US-65: full_name from Employee.full_name, or "" if no Employee. Optional
   *  for backward compat — старые тесты могут не передавать. */
  full_name?: string;
  is_staff: boolean;
  is_superuser: boolean;
  is_hijacked: boolean;
  /** Roles list from backend. Optional — backend may not include yet. */
  roles?: string[];
  /** Convenience flag: is line-manager. Optional — backend contract pending. */
  is_line_manager?: boolean;
  /** Convenience flag: is HR. Optional — backend contract pending. */
  is_hr?: boolean;
  /** Convenience flag: is project-manager (or active PM-delegate of any project). */
  is_project_manager?: boolean;
  /** Convenience flag: is PMO (role `project_office_head`). M5.2+. */
  is_pmo?: boolean;
  /** US-47.1: доступ к выгрузке 1С ЗУП — роль `zup_export` + admin/staff. */
  can_export_zup?: boolean;
  /** US-112: право дозаполнить выгрузку расчётными часами — отдельная роль
   *  `zup_fill` + admin/staff. Не совпадает с `can_export_zup`: формировать
   *  файл и дописывать в него неподтверждённые часы — разные решения. */
  can_fill_zup?: boolean;
  /** US-113: право вести историю ставок — экран «Ставки» (роль `rate_manage` + staff). */
  can_manage_rates?: boolean;
  /** US-111: доступ к резервному отчёту — роль `reserve_report` + staff. */
  can_see_reserve_report?: boolean;
  /** US-103.3: внешний согласующий без таймшита — прячем таб «Таймшит». */
  timesheet_exempt?: boolean;
  /** Реально руководитель (есть команда/проекты/PMO) = backend `user_is_manager`.
   *  Управляет видимостью «Сводки» и лендингом — синхронно с гейтом дашборда. */
  is_dashboard_manager?: boolean;
}

export const fetchMe = (): Promise<Me> => api<Me>("/api/me/");

// ---------------------------------------------------------------------------
// US-65: Self-service паролей
// ---------------------------------------------------------------------------

export interface PasswordApiResponse {
  detail: string;
  /** Список ошибок валидации пароля (для weak new_password). */
  errors?: string[];
}

/** Залогиненный пользователь меняет свой пароль. */
export const changePassword = (
  old_password: string,
  new_password: string,
): Promise<PasswordApiResponse> =>
  api<PasswordApiResponse>("/api/auth/change-password/", {
    method: "POST",
    body: JSON.stringify({ old_password, new_password }),
  });

/** Запрос на сброс пароля по email. Всегда 200 — defense against email enumeration. */
export const requestPasswordReset = (email: string): Promise<PasswordApiResponse> =>
  api<PasswordApiResponse>("/api/auth/password-reset/", {
    method: "POST",
    body: JSON.stringify({ email }),
  });

/** Установка нового пароля по одноразовой ссылке (uidb64 / token из URL). */
export const confirmPasswordReset = (
  uidb64: string,
  token: string,
  new_password: string,
): Promise<PasswordApiResponse> =>
  api<PasswordApiResponse>("/api/auth/password-reset/confirm/", {
    method: "POST",
    body: JSON.stringify({ uidb64, token, new_password }),
  });

// ---------------------------------------------------------------------------
// US-50: Workflow config (singleton, only-read from frontend)
// ---------------------------------------------------------------------------

export type TimesheetLrStage = "before_pm" | "none" | "after_pm";

export interface WorkflowConfig {
  timesheet_lr_stage: TimesheetLrStage;
}

export const fetchWorkflowConfig = (): Promise<WorkflowConfig> =>
  api<WorkflowConfig>("/api/workflow-config/");

// ---------------------------------------------------------------------------
// US-47: ZUP report — выгрузка трудозатрат в 1С ЗУП
// ---------------------------------------------------------------------------

export interface ZupReportRow {
  period: string;
  organization: string;
  user: string;
  employee: string;
  project: string;
  kind: string;
  hours: string;
  record_date: string;
  correction_date: string;
}

export interface ZupReportPreview {
  month: string;
  period_date: string;
  record_date: string;
  organization: string;
  user_full_name: string;
  rows_count: number;
  preview_rows: ZupReportRow[];
  warnings: string[];
}

export const fetchZupReportPreview = (month: string): Promise<ZupReportPreview> =>
  api<ZupReportPreview>(`/api/reports/zup/?month=${encodeURIComponent(month)}`);

/** Триггерит download xlsx-файла (открывает в той же вкладке, браузер скачает). */
export function downloadZupReportXlsx(month: string, fillPlan = false): void {
  const fill = fillPlan ? "&fill=plan" : "";
  const url = `/api/reports/zup/?month=${encodeURIComponent(month)}&download=xlsx${fill}`;
  window.location.href = url;
}

// US-112: дозаполнение выгрузки расчётными часами.
// ⚠️ В формате 1С нет колонки «Источник»: после выгрузки дозаполненные часы
// неотличимы от согласованных. Поэтому состав показывается ДО скачивания —
// этот запрос ничего не скачивает и ничего не меняет.

export interface ZupFillTarget {
  direction: string;
  project_code: string;
  hours: string;
}

export interface ZupFillEntry {
  employee: string;
  expected_hours: string;
  actual_hours: string;
  absence_hours: string;
  hours: string;
  targets: ZupFillTarget[];
}

export interface ZupFillSkipped {
  employee: string;
  hours: string;
  reason: string;
}

export interface ZupFillPlan {
  month: string;
  /** Причина, по которой дозаполнять нельзя вовсе (нет производственного
   *  календаря на месяц). Не «никого не нашли», а «считать нечем». */
  blocker: string | null;
  total_employees: number;
  total_hours: string;
  entries: ZupFillEntry[];
  skipped: ZupFillSkipped[];
}

export const fetchZupFillPlan = (month: string): Promise<ZupFillPlan> =>
  api<ZupFillPlan>(`/api/reports/zup/fill-preview/?month=${encodeURIComponent(month)}`);

// ---------------------------------------------------------------------------
// US-111 — Резервный отчёт по направлениям списания
// ---------------------------------------------------------------------------

/** Откуда взяты часы ячейки. Показывается пользователю: без этого списанные
 *  часы не отличить от расчётных, а в этом вся суть отчёта. */
export type ReserveHoursSource = "fact" | "plan" | "mixed";

export interface ReserveReportCell {
  month: string;
  /** `null` — месяц вне периода работы. Это НЕ ноль: ноль означал бы
   *  «работал и не списал ничего». */
  hours: string | null;
  source: ReserveHoursSource | null;
  /** Часы подтверждённых отсутствий за месяц. Вычтены из нормы: в 1С отпуск
   *  и больничный начисляются отдельно. Показываются отдельной колонкой —
   *  иначе при сверке непонятно, почему норма к проведению ниже календарной. */
  absence_hours?: string | null;
  source_display?: string;
  by_direction: Record<string, string>;
}

export interface ReserveReportRow {
  employee: { id: number; full_name: string; personnel_number: string };
  /** «СВЧ: 70%; Аналог: 30%», «АУР» или «—», если правило не задано. */
  where: string;
  /** Есть месяцы, которые отнести некуда. Не выводится из `where`: у человека
   *  с правилом, начинающимся позже начала периода, `where` не равен «—»,
   *  а часть месяцев всё равно без направления. */
  needs_rule: boolean;
  hire_date: string | null;
  /** US-113 (Р8): ставка числом, если перевода в периоде не было, иначе null. */
  rate: string | null;
  /** Ставка для показа: «0,50» или куски с границами при переводе —
   *  «1,00 (по 14.09.2026); 0,50 (с 15.09.2026)». */
  rate_display: string;
  cells: ReserveReportCell[];
  directions_used: string[];
}

export interface ReserveReport {
  month_from: string;
  month_to: string;
  months: string[];
  /** Норма каждого месяца на полную ставку — шапка привычного файла. */
  month_norms: Record<string, string>;
  directions: string[];
  rows: ReserveReportRow[];
  warnings: string[];
}

export const fetchReserveReport = (monthFrom: string, monthTo: string): Promise<ReserveReport> =>
  api<ReserveReport>(
    `/api/reports/reserve/?month_from=${encodeURIComponent(monthFrom)}` +
      `&month_to=${encodeURIComponent(monthTo)}`,
  );

/** URL для `<a download>` — навигация, не fetch (как у остальных выгрузок). */
export const getReserveReportXlsxUrl = (monthFrom: string, monthTo: string): string =>
  `/api/reports/reserve/?month_from=${encodeURIComponent(monthFrom)}` +
  `&month_to=${encodeURIComponent(monthTo)}&download=xlsx`;

// ---------------------------------------------------------------------------
// Timesheets (M2)
// ---------------------------------------------------------------------------

export interface TimesheetWeekSummary {
  id: number;
  employee: number;
  employee_name: string;
  week_start: string; // "YYYY-MM-DD"
  status: string;
  status_display: string;
  required_hours: string;
  allocated_hours: string;
  unallocated_hours: string;
  /**
   * US-74.1: суммарные часы approved-отсутствий, пересекающихся с неделей.
   * Нужен фронту чтобы показывать `effective_required = required - absence_hours`
   * в очереди согласования PM/LR (плашка «Меньше нормы») и в KPI.
   *
   * Optional на типе для backward-compat (старые моки в тестах без поля,
   * также legacy responses до US-74.1 деплоя). Все потребители читают через
   * `parseFloat(w.absence_hours ?? "0")`.
   */
  absence_hours?: string;
  /**
   * Часы недели, закрытые заявкой на отсутствие, которая ещё в согласовании.
   *
   * В норму они НЕ входят — пока заявку не согласовали, отпуска нет и долг
   * настоящий. Но и списать их нельзя: ввод часов на такие дни запрещён.
   * Поле нужно окну отправки, чтобы назвать причину недосписания вместо
   * совета «проверьте, не забыли ли проставить часы».
   *
   * Optional: старые ответы и моки в тестах поля не содержат.
   */
  pending_absence_hours?: string;
  /** True when the week is fully covered by an approved Absence — no timesheet work needed. */
  is_absence_only: boolean;
  submitted_at: string | null;
  approved_at?: string | null;
  lr_approved_at: string | null;
  lr_returned_at: string | null;
  lr_comment: string;
  pm_returned_at: string | null;
  pm_comment: string;
  /**
   * US-110: когда неделю заполнила система. null — заполнял человек.
   *
   * Показывается бейджем и сотруднику, и руководителю проекта. Писем об
   * автозаполнении решено не слать (решение владелицы 15.09), поэтому для
   * сотрудника этот признак — ЕДИНСТВЕННЫЙ способ узнать, что неделю за него
   * начала машина.
   *
   * Optional на типе: старые моки в тестах поля не знают.
   */
  autofilled_at?: string | null;
  /**
   * True, если actor попал в очередь ТОЛЬКО через активное делегирование
   * (line_manager_delegate в LR-очереди, pm_delegate в PM-очереди).
   * UI показывает бейдж «Передано делегату». Для своей «Таймшит»-страницы
   * (эндпоинт `/api/timesheets/`) поле всегда false — там нет queue_variant.
   */
  is_delegated_for_me?: boolean;
}

export interface TimesheetEntry {
  id: number;
  work_date: string; // "YYYY-MM-DD"
  project: number;
  project_code: string;
  project_name: string;
  /** HEX colour from Project.color (Direction C palette). Used for the row strip in the grid. */
  project_color: string;
  hours: string;
  jira_reference: string;
  comment: string;
}

export interface AbsenceDayInWeek {
  date: string; // "YYYY-MM-DD"
  hours: string; // decimal, e.g. "8.00"
}

export interface AbsenceInWeek {
  id: number;
  absence_type: AbsenceType;
  absence_type_display: string;
  start_date: string;
  end_date: string;
  days: AbsenceDayInWeek[];
  hours_in_week: string;
}

/**
 * US-53: тип дня производственного календаря.
 *
 * - `work` — обычный рабочий день, 8ч (или сколько указано).
 * - `shortened` — сокращённый рабочий, обычно 7ч (пред-праздничный).
 * - `holiday` — праздник / выходной, 0ч, нерабочий.
 * - `transfer` — перенос, 0ч если перенесён выходной (как 11.05.2026, перенос с
 *   9 мая), либо 8ч если рабочая суббота (компенсация переноса).
 */
export type CalendarDayType = "work" | "shortened" | "holiday" | "transfer";

export interface DayInWeek {
  date: string; // "YYYY-MM-DD"
  day_type: CalendarDayType;
  standard_hours: string; // "8.00" / "7.00" / "0.00"
}

/**
 * US-85: тот, у кого неделя сейчас на согласовании (вычисляется backend'ом
 * динамически по статусу и активным делегированиям).
 */
export interface PendingApprover {
  full_name: string;
  /** True → согласует активный делегат (показываем пометку «делегат»). */
  is_delegate: boolean;
}

export interface TimesheetWeekDetail extends TimesheetWeekSummary {
  entries: TimesheetEntry[];
  approved_at: string | null;
  /**
   * US-85: ФИО фактических согласантов. null когда: (а) этап ещё не пройден,
   * либо (б) автосогласование — согласант == сам сотрудник (top-level ЛР или
   * PM-автор по US-64.1): показывать «согласовал сам себя» бессмысленно.
   * Optional на типе для backward-compat (моки/legacy-ответы до US-85).
   */
  lr_approved_by_name?: string | null;
  pm_approved_by_name?: string | null;
  /**
   * US-85: у кого неделя сейчас на согласовании. Непустой только для статусов
   * `submitted_to_line_manager` (ЛР) и `waiting_project_managers` (PM проектов).
   */
  pending_with?: PendingApprover[];
  /**
   * Память согласований (13.07): id проектов недели, уже подписанных своим РП.
   * В rework-статусе на этих строках рисуем метку «✓ согласовано» —
   * правка строки снимает подпись (проект пере-согласуется).
   * Optional — старый backend поля не отдаёт.
   */
  approved_project_ids?: number[];
  /** Approved absences that intersect this week — read-only rows in the grid. */
  absences: AbsenceInWeek[];
  /**
   * US-53: производственный календарь по 7 дням недели (Mon-Sun).
   *
   * Optional на типе — старый backend (до US-53 деплоя) поле не отдаёт,
   * frontend графится с fallback'ом по weekday. Также упрощает моки в тестах,
   * не относящихся к календарю. На реальном проде после US-53 деплоя поле
   * всегда присутствует.
   */
  days?: DayInWeek[];
}

export interface ProjectOption {
  id: number;
  code: string;
  name: string;
  color: string;
}

export const fetchTimesheets = (): Promise<TimesheetWeekSummary[]> =>
  api<TimesheetWeekSummary[]>("/api/timesheets/");

export const createTimesheet = (weekStart: string): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>("/api/timesheets/", {
    method: "POST",
    body: JSON.stringify({ week_start: weekStart }),
  });

export const fetchTimesheetDetail = (id: number): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>(`/api/timesheets/${id}/`);

export const createEntry = (
  weekId: number,
  data: {
    work_date: string;
    project: number;
    hours: string;
    jira_reference?: string;
    comment?: string;
  },
): Promise<TimesheetEntry> =>
  api<TimesheetEntry>(`/api/timesheets/${weekId}/entries/`, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateEntry = (
  weekId: number,
  entryId: number,
  data: Partial<{
    work_date: string;
    project: number;
    hours: string;
    jira_reference: string;
    comment: string;
  }>,
): Promise<TimesheetEntry> =>
  api<TimesheetEntry>(`/api/timesheets/${weekId}/entries/${entryId}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deleteEntry = (weekId: number, entryId: number): Promise<void> =>
  api<void>(`/api/timesheets/${weekId}/entries/${entryId}/`, { method: "DELETE" });

// US-45: удаление всей строки проекта в неделе (все entries по паре week+project).
export const deleteProjectRow = (
  weekId: number,
  projectId: number,
): Promise<{ deleted_count: number; project_id: number }> =>
  api<{ deleted_count: number; project_id: number }>(
    `/api/timesheets/${weekId}/entries/?project_id=${projectId}`,
    { method: "DELETE" },
  );

// US-44: бейджи в навигации SPA. Эндпоинт polled каждые 30s.
export interface TimesheetActionWeek {
  /** Понедельник недели, YYYY-MM-DD. */
  week_start: string;
  /** rework_requested_by_line_manager | rework_requested_by_project_manager | rolled_back_by_admin */
  status: string;
}

export interface NotificationBadges {
  lr_timesheet_queue: number;
  pm_timesheet_queue: number;
  lr_absence_queue: number;
  /** US-107: заявки, ждущие подписи actor'а как руководителя проекта.
   *  Optional — старый бэкенд поля не отдаёт. */
  pm_absence_queue?: number;
  hr_absence_queue: number;
  my_timesheet_action_required: number;
  my_absence_action_required: number;
  /** Недели за счётчиком my_timesheet_action_required — для плашки-перехода
   * «Ждёт исправления → Открыть». Опционально (старый бэкенд поля не отдаёт). */
  my_timesheet_action_weeks?: TimesheetActionWeek[];
}

export const fetchNotificationBadges = (): Promise<NotificationBadges> =>
  api<NotificationBadges>("/api/notifications/badges/");

export const fetchProjects = (): Promise<ProjectOption[]> => api<ProjectOption[]>("/api/projects/");

// US-100: поиск сотрудников для command-palette ⌘K. Scoped по правам актора
// (рядовой видит только себя). Пустой/короткий запрос → пустой список.
export interface EmployeeSearchResult {
  id: number;
  full_name: string;
  position: string;
}

export const fetchEmployeeSearch = (q: string): Promise<EmployeeSearchResult[]> =>
  api<EmployeeSearchResult[]>(`/api/employees/search/?q=${encodeURIComponent(q)}`);

// US-95: self-service делегирование (ЛР / PM). Кандидаты в делегаты — любой
// активный сотрудник (purpose=delegate снимает report-scope-ограничение).
export const searchDelegateCandidates = (q: string): Promise<EmployeeSearchResult[]> =>
  api<EmployeeSearchResult[]>(`/api/employees/search/?purpose=delegate&q=${encodeURIComponent(q)}`);

export interface DelegateRef {
  id: number;
  full_name: string;
}
export interface LrDelegation {
  delegate: DelegateRef | null;
  active_from: string | null;
  active_to: string | null;
}
export interface PmProjectDelegation {
  id: number;
  code: string;
  name: string;
  delegate: DelegateRef | null;
  active_from: string | null;
  active_to: string | null;
}
export interface MyDelegations {
  can_delegate: { lr: boolean; pm: boolean };
  lr?: LrDelegation;
  pm?: { projects: PmProjectDelegation[] };
}
export interface DelegationInput {
  delegate_id: number | null;
  active_from?: string | null;
  active_to?: string | null;
}

export const fetchMyDelegations = (): Promise<MyDelegations> =>
  api<MyDelegations>("/api/me/delegations/");

export const setLrDelegate = (body: DelegationInput): Promise<LrDelegation> =>
  api<LrDelegation>("/api/me/delegations/lr/", { method: "PUT", body: JSON.stringify(body) });

export interface PmDelegationResult {
  delegate: DelegateRef | null;
  active_from: string | null;
  active_to: string | null;
  projects: { id: number; code: string; name: string }[];
}
export const setPmDelegate = (body: DelegationInput): Promise<PmDelegationResult> =>
  api<PmDelegationResult>("/api/me/delegations/pm/", { method: "PUT", body: JSON.stringify(body) });

// ---------------------------------------------------------------------------
// US-113: история ставки сотрудника (экран «Ставки»). Доступ — can_manage_rates.
// ---------------------------------------------------------------------------

/** Поиск для экрана «Ставки»: вся компания, включая уволенных. */
export interface RateEmployeeSearchResult extends EmployeeSearchResult {
  is_active: boolean;
  is_test?: boolean;
}

export const searchRateEmployees = (q: string): Promise<RateEmployeeSearchResult[]> =>
  api<RateEmployeeSearchResult[]>(
    `/api/employees/search/?purpose=rates&q=${encodeURIComponent(q)}`,
  );

export interface EmploymentRateRow {
  id: number;
  /** Ставка строкой с двумя знаками: "0.50". */
  rate: string;
  /** null — строка «с начала» (ставка на всё время до первого перевода). */
  date_from: string | null;
  is_initial: boolean;
  is_current: boolean;
  note: string;
  created_by: string | null;
  created_at: string | null;
}

export interface EmployeeRates {
  employee: {
    id: number;
    full_name: string;
    position: string;
    is_active: boolean;
    hire_date: string | null;
    termination_date: string | null;
  };
  current_rate: string | null;
  rates: EmploymentRateRow[];
}

/** Что система сама НЕ исправит при переводе с даты (до сохранения). */
export interface RateImpact {
  /** Начало затронутого интервала; null — с начала работы. */
  date_from: string | null;
  /** Последний день интервала (включительно): день перед следующим переводом
   *  или дата увольнения, если она раньше; null — ни того, ни другого. */
  date_to: string | null;
  /** Конец интервала задан датой увольнения, а не следующим переводом. */
  ends_at_termination?: boolean;
  /** Интервал целиком после увольнения: изменение на норму не влияет. */
  after_termination?: boolean;
  termination_date?: string | null;
  /** Согласованные недели: заморожены, новая ставка к ним не применится. */
  approved_weeks: { id: number; week_start: string; week_end: string; required_hours: string }[];
  /** Автозаполненные недели: норма пересчитается, списание останется прежним. */
  autofilled_weeks: {
    id: number;
    week_start: string;
    status: MonthlyWeekStatus;
    allocated_hours: string;
  }[];
  /** Месяцы, за которые уже выполнялось дозаполнение выгрузки в 1С. */
  zup_fill_months: { month: string; includes_employee: boolean; last_filled_at: string | null }[];
}

export interface RateInput {
  rate?: string;
  date_from?: string | null;
  note?: string;
}

export const fetchEmployeeRates = (employeeId: number): Promise<EmployeeRates> =>
  api<EmployeeRates>(`/api/employees/${employeeId}/rates/`);

/** `rowId` — правка или удаление этой строки: сервер считает последствия от
 *  меньшей из её старой и новой даты до следующего перевода. */
export const fetchRateImpact = (
  employeeId: number,
  dateFrom: string | null,
  rowId?: number,
): Promise<RateImpact> =>
  api<RateImpact>(
    `/api/employees/${employeeId}/rates/impact/?date_from=${encodeURIComponent(dateFrom ?? "")}` +
      (rowId !== undefined ? `&row=${rowId}` : ""),
  );

export const createEmployeeRate = (employeeId: number, body: RateInput): Promise<EmployeeRates> =>
  api<EmployeeRates>(`/api/employees/${employeeId}/rates/`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const updateEmployeeRate = (rowId: number, body: RateInput): Promise<EmployeeRates> =>
  api<EmployeeRates>(`/api/employee-rates/${rowId}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const deleteEmployeeRate = (rowId: number): Promise<EmployeeRates> =>
  api<EmployeeRates>(`/api/employee-rates/${rowId}/`, { method: "DELETE" });

// US-89: «Повторить прошлую неделю» — копирует проекты и часы прошлой недели
// в редактируемую (Jira/комментарии не копируются, submit остаётся ручным).
export interface CopyPreviousWeekResult {
  created: number;
  skipped_existing: number;
  skipped_absence: number;
  skipped_archived: string[];
}

export const copyPreviousWeek = (weekId: number): Promise<CopyPreviousWeekResult> =>
  api<CopyPreviousWeekResult>(`/api/timesheets/${weekId}/copy-previous-week/`, {
    method: "POST",
  });

// Approval workflow (M4.1)

export const submitTimesheet = (id: number): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>(`/api/timesheets/${id}/submit/`, { method: "POST" });

export const lrApproveTimesheet = (id: number): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>(`/api/timesheets/${id}/approve/`, { method: "POST" });

export const lrReturnTimesheet = (id: number, comment: string): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>(`/api/timesheets/${id}/return/`, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });

export const fetchTimesheetApprovalQueue = (): Promise<TimesheetWeekSummary[]> =>
  api<TimesheetWeekSummary[]>("/api/timesheets/approvals/");

// Approval workflow (M4.2) — PM

export const pmApproveTimesheet = (id: number): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>(`/api/timesheets/${id}/pm-approve/`, { method: "POST" });

export const pmReturnTimesheet = (id: number, comment: string): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>(`/api/timesheets/${id}/pm-return/`, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });

export const fetchTimesheetPMApprovalQueue = (): Promise<TimesheetWeekSummary[]> =>
  api<TimesheetWeekSummary[]>("/api/timesheets/pm-approvals/");

// Bulk approval (M-design-c) — массовое одобрение нескольких недель.

export type BulkApproveSkipReason =
  | "not_found"
  | "not_line_manager"
  | "not_project_manager"
  | "wrong_status";

export interface BulkApproveSkipped {
  id: number;
  reason: BulkApproveSkipReason;
}

export interface BulkApproveResult {
  approved: number[];
  approved_count: number;
  /**
   * US-87 (только PM bulk): недели, где actor одобрил свои проекты, но неделя
   * ещё не закрыта — ждёт согласования других РП. Optional: LR bulk их не шлёт.
   */
  partial?: number[];
  partial_count?: number;
  skipped: BulkApproveSkipped[];
  skipped_count: number;
}

export const bulkApproveTimesheetsLR = (ids: number[]): Promise<BulkApproveResult> =>
  api<BulkApproveResult>("/api/timesheets/bulk-approve/", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });

export const bulkApproveTimesheetsPM = (ids: number[]): Promise<BulkApproveResult> =>
  api<BulkApproveResult>("/api/timesheets/pm-bulk-approve/", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });

// ---------------------------------------------------------------------------
// Absences (M3)
// ---------------------------------------------------------------------------

// US-78: общий day_off разведён на 3 подтипа отгула.
// vacation_unpaid: «Отпуск за свой счёт» — поведение как у планового отпуска
// (vacation), различие только для кадрового оформления HR.
export type AbsenceType =
  | "vacation"
  | "vacation_unpaid"
  | "day_off_vacation"
  | "day_off_weekend"
  | "day_off_unpaid"
  | "sick_leave";

export type AbsenceStatus =
  | "draft"
  // US-107: ступень руководителя проекта. Появляется только у сотрудников
  // в команде проекта с включённым согласованием отсутствий; у остальных
  // маршрут прежний и начинается сразу с "submitted".
  | "submitted_to_pm"
  | "submitted"
  | "approved_by_lr"
  | "approved"
  | "returned";

export interface AbsenceEmployeeRef {
  id: number;
  full_name: string;
}

/** Шаг маршрута согласования отсутствия (карточка «у кого сейчас / что дальше»). */
export interface AbsenceRouteStep {
  // US-107: шаг "pm" появляется только у заявок, реально проходящих ступень
  // руководителя проекта.
  key: "submit" | "pm" | "lr" | "hr" | "done";
  label: string;
  actor: string | null;
  state: "done" | "current" | "pending" | "returned";
}

export interface Absence {
  id: number;
  absence_type: AbsenceType;
  absence_type_display: string;
  start_date: string; // "YYYY-MM-DD"
  end_date: string;
  working_days: number;
  /** Календарных дней включительно — вторая цифра для HR-оформления.
   *  Optional для обратной совместимости старых моков. */
  calendar_days?: number;
  hours: number;
  status: AbsenceStatus;
  status_display: string;
  /**
   * US-108: дата подачи приходит И в списке, и в detail — по ней упорядочена
   * очередь согласования («кто ждёт дольше всех» первым).
   *
   * Optional оставлено осознанно: у черновика и у заявки, заведённой через
   * админку, даты нет — такие строки сортировка кладёт вниз.
   *
   * Важно держать поле здесь, а не среди detail-only: пока оно значилось
   * «только в detail», сортировка по нему молча не работала — значения в
   * ответе списка не было, компаратор возвращал 0 для всех пар.
   */
  submitted_at?: string | null;
  // Detail-only поля (в списках их нет) — отмечены optional, не ломают
  // существующие тесты, в которых список возвращается с тонким набором полей.
  comment?: string;
  lr_comment?: string;
  hr_comment?: string;
  lr_approved_at?: string | null;
  hr_approved_at?: string | null;
  /**
   * US-88: ФИО согласантов на этапах ЛР и HR. null, если этап ещё не пройден.
   * Есть и в списке/очереди (тонкий сериализатор), и в detail. Для HR-этапа
   * особенно важно — заявку согласует любой из HR, фиксируем кто именно.
   */
  lr_approved_by_name?: string | null;
  hr_approved_by_name?: string | null;
  /** US-107: комментарий РП при возврате. Показывается сотруднику и,
   *  если заявка дошла до ЛР, — линейному руководителю при решении. */
  pm_comment?: string;
  /** US-107: проекты, чьей подписи ещё ждём. Непусто только в статусе
   *  submitted_to_pm — при нескольких РП без этого непонятно, почему
   *  заявка стоит. */
  pm_pending_projects?: { id: number; code: string; name: string }[];
  /** US-107: кто уже подписал, по проектам. */
  pm_approvals?: {
    project_id: number;
    project_code: string;
    approved_by_name: string | null;
    approved_at: string;
    /** US-107: необязательная оговорка РП при согласовании — её читает ЛР. */
    comment?: string;
  }[];
  employee: AbsenceEmployeeRef;
  /**
   * True, если actor попал в LR-очередь заявки через активное делегирование
   * `line_manager_delegate`. Для HR-очереди всегда false (HrDelegate — отдельная
   * сущность, бейджем не покрывается). Для списка «своих» заявок (эндпоинт
   * `/api/absences/`) поле тоже false — там нет queue_variant.
   */
  is_delegated_for_me?: boolean;
  /** US-66: к заявке прикреплён файл-заявление. */
  has_attachment?: boolean;
  /** US-66: оригинальное имя файла (basename). null если файла нет. */
  attachment_name?: string | null;
  /** US-66: размер файла в байтах. Только в detail-сериализаторе. */
  attachment_size_bytes?: number | null;
  /** US-66: абсолютный URL на скачивание (через permission-checked endpoint).
   *  Только в detail. */
  attachment_url?: string | null;
  /** Маршрут согласования — только в detail (/api/absences/<id>/). */
  approval_route?: AbsenceRouteStep[];
}

export interface AbsenceCreatePayload {
  absence_type: AbsenceType;
  start_date: string;
  end_date: string;
  comment?: string;
}

export type AbsenceUpdatePayload = Partial<AbsenceCreatePayload>;

export const fetchAbsences = (): Promise<Absence[]> => api<Absence[]>("/api/absences/");

export const fetchAbsence = (id: number): Promise<Absence> => api<Absence>(`/api/absences/${id}/`);

export const createAbsence = (data: AbsenceCreatePayload): Promise<Absence> =>
  api<Absence>("/api/absences/", {
    method: "POST",
    body: JSON.stringify(data),
  });

export const updateAbsence = (id: number, data: AbsenceUpdatePayload): Promise<Absence> =>
  api<Absence>(`/api/absences/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });

export const deleteAbsence = (id: number): Promise<void> =>
  api<void>(`/api/absences/${id}/`, { method: "DELETE" });

export const submitAbsence = (id: number): Promise<Absence> =>
  api<Absence>(`/api/absences/${id}/submit/`, { method: "POST" });

/** Владелец отзывает заявку с согласования (submitted/approved_by_lr → draft). */
export const withdrawAbsence = (id: number): Promise<Absence> =>
  api<Absence>(`/api/absences/${id}/withdraw/`, { method: "POST" });

export const approveAbsence = (id: number): Promise<Absence> =>
  api<Absence>(`/api/absences/${id}/approve/`, { method: "POST" });

export const returnAbsence = (id: number, comment: string): Promise<Absence> =>
  api<Absence>(`/api/absences/${id}/return/`, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });

export const fetchApprovalQueue = (): Promise<Absence[]> =>
  api<Absence[]>("/api/absences/approvals/");

/** US-107: очередь руководителя проекта. Заявка остаётся здесь у тех РП,
 *  кто ещё не подписал: подписавший её больше не видит, остальные видят —
 *  пока не подпишут все. */
export const fetchAbsencePMApprovalQueue = (): Promise<Absence[]> =>
  api<Absence[]>("/api/absences/pm-approvals/");

/** US-107: РП согласует заявку по своим проектам. Пока подписали не все,
 *  заявка остаётся на ступени РП.
 *
 *  `comment` — необязательная оговорка («согласую, но период плотный»);
 *  её увидит линейный руководитель, когда заявка дойдёт до него. */
export const pmApproveAbsence = (id: number, comment?: string): Promise<Absence> =>
  api<Absence>(`/api/absences/${id}/pm-approve/`, {
    method: "POST",
    body: JSON.stringify({ comment: comment ?? "" }),
  });

/** US-107: РП возвращает заявку сотруднику. До ЛР она не доходит —
 *  это и есть право вето на сроки. */
export const pmReturnAbsence = (id: number, comment: string): Promise<Absence> =>
  api<Absence>(`/api/absences/${id}/pm-return/`, {
    method: "POST",
    body: JSON.stringify({ comment }),
  });

/** Типы отсутствия, для которых система генерирует печатный бланк заявления.
 *  vacation_unpaid и day_off_unpaid используют общий бланк «без сохранения зп». */
export const APPLICATION_ABSENCE_TYPES: AbsenceType[] = [
  "vacation",
  "vacation_unpaid",
  "day_off_unpaid",
];

export const hasAbsenceApplication = (type: AbsenceType): boolean =>
  APPLICATION_ABSENCE_TYPES.includes(type);

/**
 * URL автозаполненного заявления по сохранённой заявке. По умолчанию —
 * редактируемый Word (.docx, скачивается); `format="html"` — печатный HTML.
 * Не делает fetch.
 */
export const getAbsenceApplicationUrl = (id: number, format: "docx" | "html" = "docx"): string =>
  `/api/absences/${id}/application/${format === "docx" ? "?download=docx" : ""}`;

/**
 * URL заявления из ещё НЕ сохранённой формы (по типу+датам). По умолчанию —
 * редактируемый Word (.docx). Для кнопки «Сформировать заявление» в форме.
 */
export const getMyAbsenceApplicationUrl = (p: {
  type: AbsenceType;
  start: string;
  end: string;
  format?: "docx" | "html";
}): string => {
  const fmt = p.format === "html" ? "" : "&download=docx";
  return (
    `/api/me/absence-application/?type=${encodeURIComponent(p.type)}` +
    `&start=${encodeURIComponent(p.start)}&end=${encodeURIComponent(p.end)}${fmt}`
  );
};

// ---------------------------------------------------------------------------
// US-66: file attachment к Absence
// ---------------------------------------------------------------------------
//
// Upload — multipart/form-data; api() жёстко ставит Content-Type=json, поэтому
// здесь работаем напрямую с fetch. Download — отдельный путь через blob, потому
// что api() пытается parse JSON. Backend кладёт permission-check внутрь
// `AbsenceAttachmentView`, поэтому stranger получит 404, ЛР/HR/админ — 200.

/**
 * Загрузить (или заменить) файл-заявление к заявке на отсутствие.
 * Доступно только владельцу заявки в статусе draft/returned.
 * Возвращает обновлённый Absence (с актуальными attachment_*).
 */
export async function uploadAbsenceAttachment(id: number, file: File): Promise<Absence> {
  const form = new FormData();
  form.append("attachment", file);
  const csrf = getCookie("skedd_csrftoken");
  const headers: HeadersInit = {};
  if (csrf) headers["X-CSRFToken"] = csrf;
  const response = await fetch(`/api/absences/${id}/attachment/`, {
    method: "POST",
    body: form,
    credentials: "same-origin",
    headers,
    // NB: НЕ ставим Content-Type — fetch добавит multipart-boundary сам.
  });
  if (!response.ok) {
    let detail = "";
    let body: Record<string, unknown> | null = null;
    try {
      body = (await response.clone().json()) as Record<string, unknown>;
      if (body && typeof body["detail"] === "string") detail = body["detail"] as string;
    } catch {
      // не JSON — используем статус
    }
    const err = new Error(detail || `Не удалось загрузить файл: ${response.status}`) as Error & {
      status?: number;
      body?: Record<string, unknown> | null;
    };
    err.status = response.status;
    err.body = body;
    throw err;
  }
  return (await response.json()) as Absence;
}

/**
 * Скачать файл-заявление. Создаёт временный blob-URL и кликает по
 * `<a download>`. Permission-check — на backend. 404 если файла нет.
 */
export async function downloadAbsenceAttachment(
  id: number,
  fallbackName: string = "absence.pdf",
): Promise<void> {
  const response = await fetch(`/api/absences/${id}/attachment/`, {
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new Error(`Не удалось скачать файл: ${response.status}`);
  }
  // Достаём имя файла из Content-Disposition (если backend проставил).
  const disposition = response.headers.get("Content-Disposition") || "";
  let filename = fallbackName;
  // Сначала ищем filename*=, потом filename=
  const starMatch = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(disposition);
  if (starMatch && starMatch[1]) {
    try {
      filename = decodeURIComponent(starMatch[1]);
    } catch {
      // оставляем fallback
    }
  } else {
    const plainMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
    if (plainMatch && plainMatch[1]) filename = plainMatch[1];
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Освобождаем blob-URL через 1s (даём браузеру начать скачивание).
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Удалить прикреплённый файл (без удаления самой заявки). 204 при успехе.
 * Доступно владельцу в editable-статусе.
 */
export const deleteAbsenceAttachment = (id: number): Promise<void> =>
  api<void>(`/api/absences/${id}/attachment/`, { method: "DELETE" });

// ---------------------------------------------------------------------------
// History (M5.1) — read-only просмотр прошлых недель/абсансов в зоне actor'а.
// Permission-матрица применяется на бэкенде (apps/api/permissions.py).
// ---------------------------------------------------------------------------

/** Элемент списка истории таймшитов. Формат совпадает с `TimesheetWeekSummary`. */
export type TimesheetHistoryItem = TimesheetWeekSummary;

/** Элемент списка истории абсансов. Формат совпадает с `Absence` (list-вариант). */
export type AbsenceHistoryItem = Absence;

export interface HistoryQuery {
  /** YYYY-MM-DD, обязательный */
  date_from: string;
  /** YYYY-MM-DD, обязательный */
  date_to: string;
  /** Фильтр по сотруднику. Для employee игнорируется (всегда «свои»). */
  employee_id?: number;
}

function buildHistoryQuery(q: HistoryQuery): string {
  const params = new URLSearchParams({ date_from: q.date_from, date_to: q.date_to });
  if (q.employee_id != null) params.set("employee_id", String(q.employee_id));
  return params.toString();
}

export const fetchHistoryTimesheets = (q: HistoryQuery): Promise<TimesheetHistoryItem[]> =>
  api<TimesheetHistoryItem[]>(`/api/history/timesheets/?${buildHistoryQuery(q)}`);

export const fetchHistoryTimesheet = (id: number): Promise<TimesheetWeekDetail> =>
  api<TimesheetWeekDetail>(`/api/history/timesheets/${id}/`);

export const fetchHistoryAbsences = (q: HistoryQuery): Promise<AbsenceHistoryItem[]> =>
  api<AbsenceHistoryItem[]>(`/api/history/absences/?${buildHistoryQuery(q)}`);

export const fetchHistoryAbsence = (id: number): Promise<Absence> =>
  api<Absence>(`/api/history/absences/${id}/`);

// ---------------------------------------------------------------------------
// Weekly summary report (M5.2) — агрегат по одной неделе, scope на бэкенде.
// ---------------------------------------------------------------------------

// US-78: 3 подтипа отгула + катч-олл "other". vacation_unpaid — подтип отпуска.
export type WeeklyReportAbsenceType =
  | "vacation"
  | "vacation_unpaid"
  | "day_off_vacation"
  | "day_off_weekend"
  | "day_off_unpaid"
  | "sick_leave"
  | "other";

export interface WeeklyReportProject {
  id: number;
  code: string;
  name: string;
}

export interface WeeklyReportProjectHours {
  project_id: number;
  project_code: string;
  project_name: string;
  hours: string; // decimal "NN.NN"
}

export interface WeeklyReportEmployee {
  id: number;
  full_name: string;
}

export interface WeeklyReportRow {
  employee: WeeklyReportEmployee;
  timesheet_status: string;
  timesheet_status_display: string;
  project_hours: WeeklyReportProjectHours[];
  absence_hours_by_type: Partial<Record<WeeklyReportAbsenceType, string>>;
  absence_hours_total: string;
  allocated_hours: string;
  unallocated_hours: string;
  required_hours: string;
  total_hours: string;
}

export interface WeeklyReportTotals {
  project_hours: WeeklyReportProjectHours[];
  absence_hours_by_type: Partial<Record<WeeklyReportAbsenceType, string>>;
  allocated: string;
  unallocated: string;
  required: string;
  total: string;
}

export interface WeeklyReport {
  week_start: string; // YYYY-MM-DD
  week_end: string;
  projects: WeeklyReportProject[];
  /** absence-типы в порядке отображения; только те, что встретились в данных. */
  absence_types: WeeklyReportAbsenceType[];
  rows: WeeklyReportRow[];
  totals: WeeklyReportTotals;
}

/**
 * Категория персонала (07.09): производственный работает на проектах,
 * непроизводственный обеспечивает работу компании (бухгалтерия, кадры,
 * юристы), внешний — не сотрудник компании.
 */
export type PersonnelCategory = "production" | "non_production" | "external";

/** Подписи для выбора категории в отчётах — порядок как в админке. */
export const PERSONNEL_CATEGORY_OPTIONS: { value: PersonnelCategory; label: string }[] = [
  { value: "production", label: "Производственный" },
  { value: "non_production", label: "Непроизводственный" },
  { value: "external", label: "Внешний" },
];

export interface WeeklyReportQuery {
  /** YYYY-MM-DD, обязателен, понедельник. */
  weekStart: string;
  /** Фильтр по сотруднику; 404 если вне scope. */
  employeeId?: number;
  /** Фильтр по проекту: только сотрудники с часами на нём. */
  projectId?: number;
  /**
   * Фильтр по категории персонала. Без него отчёт прежний — тех, кто не ведёт
   * таймшит, в нём нет. Явно запрошенная непроизводственная или внешняя
   * категория показывает и их: иначе на осмысленный запрос был бы пустой лист.
   */
  category?: PersonnelCategory;
}

export const fetchWeeklyReport = (q: WeeklyReportQuery): Promise<WeeklyReport> => {
  const params = new URLSearchParams({ week_start: q.weekStart });
  if (q.employeeId != null) params.set("employee_id", String(q.employeeId));
  if (q.projectId != null) params.set("project_id", String(q.projectId));
  if (q.category) params.set("category", q.category);
  return api<WeeklyReport>(`/api/reports/weekly/?${params.toString()}`);
};

// ---------------------------------------------------------------------------
// Monthly summary report (M5.3) — агрегат по календарному месяцу.
// ---------------------------------------------------------------------------

export type MonthlyWeekStatus =
  | "draft"
  | "submitted_to_line_manager"
  | "rework_requested_by_line_manager"
  | "waiting_project_managers"
  | "rework_requested_by_project_manager"
  | "approved"
  | "rolled_back_by_admin"
  | "absence_only"
  | "no_week"
  // Синтетический: сотрудник освобождён от таймшита. Не «не заполнил», а
  // «не обязан» — разные вещи, и в своде их нельзя показывать одинаково.
  | "timesheet_exempt";

export interface MonthlyReportWeekStatus {
  week_start: string; // "YYYY-MM-DD"
  status: MonthlyWeekStatus;
  status_display: string;
}

export interface MonthlyReportRow {
  employee: WeeklyReportEmployee;
  weeks_breakdown: MonthlyReportWeekStatus[];
  project_hours: WeeklyReportProjectHours[];
  /** Monthly row контракт: ВСЕ 4 ключа absence всегда присутствуют (возможно "0.00"). */
  absence_hours_by_type: Record<WeeklyReportAbsenceType, string>;
  absence_hours_total: string;
  allocated_hours: string;
  unallocated_hours: string;
  required_hours: string;
  total_hours: string;
}

export interface MonthlyReportTotals {
  project_hours: WeeklyReportProjectHours[];
  absence_hours_by_type: Record<WeeklyReportAbsenceType, string>;
  absence_hours_total: string;
  allocated_hours: string;
  unallocated_hours: string;
  required_hours: string;
  total_hours: string;
}

export interface MonthlyReport {
  month: string; // "YYYY-MM"
  month_start: string; // "YYYY-MM-01"
  month_end: string; // "YYYY-MM-DD"
  projects: WeeklyReportProject[];
  absence_types: WeeklyReportAbsenceType[];
  rows: MonthlyReportRow[];
  totals: MonthlyReportTotals;
}

export interface MonthlyReportQuery {
  /** "YYYY-MM" — обязателен. */
  month: string;
  /** Фильтр по сотруднику; 404 если вне scope. */
  employeeId?: number;
  /** Фильтр по проекту: только сотрудники с часами на нём. */
  projectId?: number;
  /** Фильтр по категории персонала — см. `WeeklyReportQuery.category`. */
  category?: PersonnelCategory;
}

export const fetchMonthlyReport = (q: MonthlyReportQuery): Promise<MonthlyReport> => {
  const params = new URLSearchParams({ month: q.month });
  if (q.employeeId != null) params.set("employee_id", String(q.employeeId));
  if (q.projectId != null) params.set("project_id", String(q.projectId));
  if (q.category) params.set("category", q.category);
  return api<MonthlyReport>(`/api/reports/monthly/?${params.toString()}`);
};

// ---------------------------------------------------------------------------
// Exceptions report (M5.4 / US-17) — фильтрованный weekly: «не закрыли период».
// ---------------------------------------------------------------------------

export type ExceptionKind = "not_closed" | "underbooked";

export interface ExceptionsReportRow {
  employee: WeeklyReportEmployee;
  timesheet_status: string;
  timesheet_status_display: string;
  exception_kind: ExceptionKind;
  absence_hours_by_type: Partial<Record<WeeklyReportAbsenceType, string>>;
  absence_hours_total: string;
  allocated_hours: string;
  unallocated_hours: string;
  required_hours: string;
  total_hours: string;
}

export interface ExceptionsReportTotals {
  absence_hours_by_type: Partial<Record<WeeklyReportAbsenceType, string>>;
  absence_hours_total: string;
  allocated_hours: string;
  unallocated_hours: string;
  required_hours: string;
  total_hours: string;
}

export interface ExceptionsReport {
  week_start: string; // "YYYY-MM-DD"
  week_end: string;
  absence_types: WeeklyReportAbsenceType[];
  rows: ExceptionsReportRow[];
  totals: ExceptionsReportTotals;
}

export interface ExceptionsReportQuery {
  weekStart: string;
  employeeId?: number;
}

export const fetchExceptionsReport = (q: ExceptionsReportQuery): Promise<ExceptionsReport> => {
  const params = new URLSearchParams({ week_start: q.weekStart });
  if (q.employeeId != null) params.set("employee_id", String(q.employeeId));
  return api<ExceptionsReport>(`/api/reports/exceptions/?${params.toString()}`);
};

// ---------------------------------------------------------------------------
// xlsx download URL helpers (M5.3, расширен в M5.4 для exceptions)
// ---------------------------------------------------------------------------

export type ReportKind = "weekly" | "monthly" | "exceptions";

export interface ReportXlsxUrlParams {
  kind: ReportKind;
  weekStart?: string; // "YYYY-MM-DD" — для weekly + exceptions
  month?: string; // "YYYY-MM" — для monthly
  employeeId?: number;
  projectId?: number; // фильтр по проекту (weekly/monthly)
  /**
   * Фильтр по категории персонала (weekly/monthly).
   *
   * Обязателен к передаче, если он выбран на экране: файл должен совпадать с
   * тем, что видно. Без него выгрузка отдавала строго ДРУГОЙ набор —
   * непроизводственных в ней не было вовсе, зато был весь производственный
   * персонал, и под тем же именем файла.
   */
  category?: PersonnelCategory;
}

/**
 * Возвращает URL вида `/api/reports/{weekly|monthly|exceptions}/?...&download=xlsx`
 * для `<a href download>`-навигации. Не делает fetch.
 *
 * Используем `?download=xlsx` (а не `?format=xlsx`), потому что DRF
 * резервирует `?format=` под content-negotiation и поднимает 404, если
 * запрошенный формат не зарегистрирован renderer'ом.
 */
export const getReportXlsxUrl = (p: ReportXlsxUrlParams): string => {
  const params = new URLSearchParams();
  if (p.kind === "monthly") {
    if (p.month) params.set("month", p.month);
  } else {
    if (p.weekStart) params.set("week_start", p.weekStart);
  }
  if (p.employeeId != null) params.set("employee_id", String(p.employeeId));
  if (p.projectId != null && p.kind !== "exceptions") {
    params.set("project_id", String(p.projectId));
  }
  if (p.category && p.kind !== "exceptions") {
    params.set("category", p.category);
  }
  params.set("download", "xlsx");
  const base =
    p.kind === "weekly"
      ? "/api/reports/weekly/"
      : p.kind === "monthly"
        ? "/api/reports/monthly/"
        : "/api/reports/exceptions/";
  return `${base}?${params.toString()}`;
};

// ---------------------------------------------------------------------------
// US-93 — Дашборд руководителя
// ---------------------------------------------------------------------------

export interface DashboardKpi {
  /** ЭПИК C 1.1: = бейджу «На согласовании» (таймшиты ЛР+PM + отсутствия ЛР+HR). */
  pending_approvals: number;
  /** count = ровно длина not_submitted_list (карточка и блок не расходятся). */
  not_submitted: { count: number; total: number };
  /** Сумма недозаполненных часов по СОГЛАСОВАННЫМ неделям, строка "NN.NN". */
  hours_debt: string;
  /** ЭПИК C: ЗАЯВОК в отсутствии СЕГОДНЯ (= строкам экрана US-103). */
  absent_now: number;
  /** ЭПИК C: ЗАЯВОК со стартом в ближайшие 30 дней (= строкам экрана). */
  absent_upcoming_30d: number;
  /** @deprecated старое окно «эта+след. неделя»; использовать absent_now. */
  absent_two_weeks: number;
}

export interface DashboardEmployeeRow {
  employee_id: number;
  full_name: string;
  initials: string;
  timesheet_status: string;
  timesheet_status_display: string;
  can_remind: boolean;
  /** Почему человек в моём списке: подчинённый ("lr") и/или на моём проекте ("pm"). */
  roles?: ("lr" | "pm")[];
}

export interface DashboardSection {
  /** "line_manager" | "project_manager" | "all". */
  role: string;
  title: string;
  week_start: string;
  week_end: string;
  employees: DashboardEmployeeRow[];
}

export interface ManagerDashboard {
  greeting_name: string;
  chase_week_start: string;
  chase_week_end: string;
  kpi: DashboardKpi;
  /** ЭПИК C 1.1: false у чистого РП — карточка «Недосписанные часы» скрывается
   * (норму часов гоняет ЛР). Отсутствие поля = true (совместимость). */
  show_hours_debt?: boolean;
  /** ЭПИК C: единый список «не сдали» без дублей (с roles-бейджами). */
  not_submitted_list: DashboardEmployeeRow[];
  /** @deprecated старые раздельные секции; рендерится not_submitted_list. */
  not_submitted_sections: DashboardSection[];
}

export interface RemindResult {
  sent: number;
  skipped_no_login: number;
  skipped_throttled: number;
  skipped_out_of_scope: number;
  skipped_optout: number;
}

export const fetchManagerDashboard = (): Promise<ManagerDashboard> =>
  api<ManagerDashboard>("/api/dashboard/");

export const remindUnsubmitted = (p: {
  employeeIds: number[];
  weekStart: string;
}): Promise<RemindResult> =>
  api<RemindResult>("/api/dashboard/remind/", {
    method: "POST",
    body: JSON.stringify({ employee_ids: p.employeeIds, week_start: p.weekStart }),
  });

// US-103: отсутствия команды (экран за KPI-карточкой «Отсутствуют»).
export interface TeamAbsenceRow {
  /** id заявки — для клика → карточка маршрута (/api/absences/<id>/). */
  id?: number;
  employee: { id: number; full_name: string; position: string };
  absence_type: string;
  absence_type_display: string;
  start_date: string;
  end_date: string;
  calendar_days: number;
  working_days: number;
  status?: AbsenceStatus;
  /** Только в блоке `current`: календарных дней до выхода включительно. */
  days_left?: number;
  /** Заявка ещё на согласовании у HR (approved_by_lr) — показываем серым «ждёт HR». */
  pending_hr?: boolean;
  /** Заявка ещё на согласовании у ЛР (submitted) — показываем серым «ждёт ЛР». */
  pending_lr?: boolean;
  /** US-107: заявка на ступени руководителя проекта (submitted_to_pm).
   *  Отдельный признак, а не «ждёт ЛР»: иначе линейный руководитель идёт
   *  в свою очередь и не находит там заявку — она у РП. */
  pending_pm?: boolean;
}

export interface TeamAbsences {
  today: string;
  horizon_days: number;
  /** Отсутствуют сейчас (период включает сегодня), сорт. по дате выхода. */
  current: TeamAbsenceRow[];
  /** Предстоящие в ближайшие `horizon_days` дней, сорт. по дате начала. */
  upcoming: TeamAbsenceRow[];
}

export const fetchTeamAbsences = (): Promise<TeamAbsences> =>
  api<TeamAbsences>("/api/dashboard/absences/");
